import React, { useEffect, useState } from 'react';

// components
import { DropdownSelectComponent } from 'baseComponents/DropdownSelectComponent';
import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { MultiSelectDropdownComponent } from 'baseComponents/MultiSelectDropdownComponent';
import { FullScreenLoaderComponent } from 'baseComponents/FullScreenLoaderComponent';
import { ResultPage } from 'components/ResultPage';

// screens
import { TabContentWrapper } from 'screens/CreateCampaignScreen/components/TabContentWrapper';
import { CreateAudienceForm } from 'screens/CreateCampaignScreen/components/CreateAudienceForm';
import { UseExistingAudienceTable } from 'screens/CreateCampaignScreen/components/UseExistingAudienceTable';

// store
import {
  useCreateCampaignStore,
  useCreateCampaignStoreActions,
} from 'store/CreateCampaignStore';
import {
  useCreateNewAudienceActions,
  useCreateNewAudienceStore,
} from 'store/CreateNewAudienceStore';
import { useUserStore } from 'store/UserStore';

// styles
import './audienceTab.scss';

// helpers
import {
  parseDataAudience,
  parseMultiSelectDataStore,
} from 'helpers/dropdown-data';
import { convertAudienceToBrazeFormat } from 'helpers/create-audience';

// constants
import { CREATE_CAMPAIGN_SCREEN } from 'constants/enums/create-campaign-screen';

// hooks
import { useGetAudienceList } from 'hooks/useGetAudienceList';
import { useGetAttributesList } from 'hooks/useGetAttributesList';
import { useCreateNewAudience } from 'hooks/useCreateNewAudience';
import {
  CampaignEstimationsResponseType,
  useEstimateAudience,
} from 'hooks/useEstimateAudience';

// types
import { AudienceType } from 'types/create-campaign';
import { notification } from 'antd';
import { convertSendDate } from 'helpers/create-campaign';
import { EstimationChannelCard } from 'screens/CreateCampaignScreen/components/EstimationChannelCard';
import { TextComponent } from 'baseComponents/TextComponent';
import { ModalComponent } from 'baseComponents/ModalComponent';
import { PurchaseCreditsTab } from 'screens/PurchaseCreditsScreen/components/PurchaseCreditsTab';
import { useGetCredits } from 'hooks/useGetCredits';

type AudienceTabProps = {
  onChannelSelect: (channel: 'sms' | 'push') => void;
};

export const AudienceTab: React.FC<AudienceTabProps> = ({
  onChannelSelect,
}) => {
  const [api, contextHolder] = notification.useNotification();

  // zustand store
  const storesList = useUserStore((state) => state.stores);
  const stores = useCreateCampaignStore((state) => state.stores);
  const audience = useCreateCampaignStore((state) => state.audience);
  const sendDate = useCreateCampaignStore((state) => state.sendDate);
  const sendTime = useCreateCampaignStore((state) => state.sendTime);

  // Seed from Zustand so that local state survives component remounts (e.g. redirectFromPayment)
  const [useExistingCampaign, setUseExistingCampaign] = useState<string | null>(
    audience ? CREATE_CAMPAIGN_SCREEN.USE_EXISTING_VALUE : null
  );
  const [chosenAudience, setChosenAudience] = useState<AudienceType | null>(
    audience ?? null
  );
  const newAudienceFilters = useCreateNewAudienceStore(
    (state) => state.audienceFilters
  );
  const newAudienceName = useCreateNewAudienceStore((state) => state.name);
  const { setEstimatedAudience, clearCreateNewAudienceStore } =
    useCreateNewAudienceActions();
  const { setStores, setAudience, setHasCredits, clearComposeFields } =
    useCreateCampaignStoreActions();

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [renderPaymentUI, setRenderPaymentUI] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // handles adding and removing stores ids to created campaign
  const handleStoreSelect = (item: string[]) => {
    setStores(item);
    if (chosenAudience) {
      handleEstimateAudience(chosenAudience?.audience_filters, item);
    }
  };

  // List of stores displayed in a dropdown.
  const multiSelectDataStore = parseMultiSelectDataStore(storesList, () => {});

  // on create new button click
  const handleCreateNewCampaign = () => {
    setUseExistingCampaign(CREATE_CAMPAIGN_SCREEN.CREATE_NEW_VALUE);
    setChosenAudience(null);
    setAudience(null);
    setHasCredits(true);
  };

  // on created audience
  const handleCreateAudienceSuccess = (response: AudienceType) => {
    refetchAudienceData();
    clearCreateNewAudienceStore();
    setUseExistingCampaign(CREATE_CAMPAIGN_SCREEN.USE_EXISTING_VALUE);
    setChosenAudience(response);

    setAudience(chosenAudience);
    handleEstimateAudience(response?.audience_filters);
    return api.success({
      message: 'Audience created',
      description: 'Audience has been created successfully',
      duration: 3,
    });
  };

  // set estimation in zustand
  const handleEstimateAudienceSuccess = (
    data: CampaignEstimationsResponseType
  ) => {
    const hasCredits = data.sms.has_credits || data.push.has_credits;
    setEstimatedAudience({
      sms: {
        estimated_reachable_users: data.sms.estimated_reachable_users,
        has_credits: data.sms.has_credits,
        credit_cost: data.sms.credit_cost,
      },
      push: {
        estimated_reachable_users: data.push.estimated_reachable_users,
        has_credits: data.push.has_credits,
        credit_cost: data.push.credit_cost,
      },
    });
    setHasCredits(hasCredits);
    if (useExistingCampaign === CREATE_CAMPAIGN_SCREEN.CREATE_NEW_VALUE) {
      api.success({
        message: 'Audience has been estimated',
        duration: 3,
      });
      return;
    }
    if (useExistingCampaign === CREATE_CAMPAIGN_SCREEN.USE_EXISTING_VALUE) {
      setAudience(chosenAudience);
      api.success({
        message: 'Audience has been estimated',
        duration: 3,
      });
    }
  };

  const createNewAudience = useCreateNewAudience({
    onSuccess: (r: AudienceType) => handleCreateAudienceSuccess(r),
    onError: (e: any) => {
      return api.error({
        message: e?.message || 'Something went wrong',
        description:
          e.response?.data.message ||
          'Something went wrong with creating audience. Please try again later.',
        duration: 3,
      });
    },
  });
  const {
    isLoading: audienceListLoading,
    error: audienceListError,
    data: audienceListData = [],
    refetch: refetchAudienceData,
  } = useGetAudienceList();
  const {
    isLoading: attributesLoading,
    error: attributesError,
    data: attributesList,
  } = useGetAttributesList();

  const handleEstimateAudienceError = (data: any) => {
    return api.error({
      message: data?.message || 'Something went wrong',
      description:
        'Something went wrong with estimating audience. Please try again later.',
      duration: 3,
    });
  };
  const {
    mutate: estimateAudience,
    isLoading: isEstimatingAudienceLoading,
    data: estimationData,
  } = useEstimateAudience({
    onSuccess: handleEstimateAudienceSuccess,
    onError: handleEstimateAudienceError,
  });

  const handleSaveAudienceTemplate = () => {
    if (newAudienceName.length > 0) {
      const payload = {
        name: newAudienceName,
        audience_filters: convertAudienceToBrazeFormat(newAudienceFilters),
      };
      createNewAudience.mutate(payload);
      handleChooseAudience();
    } else {
      return api.error({
        message: 'Something went wrong',
        description: 'Please provide a name for the new audience.',
        duration: 3,
      });
    }
  };

  const getSendDateTimestamp = () => {
    if (sendDate && sendTime) {
      return convertSendDate(sendDate, sendTime);
    }
    return null;
  };

  const handleEstimateAudience = (
    audienceFilters: any,
    storesArray?: string[]
  ) => {
    const sendDateTimestamp = getSendDateTimestamp();
    if (sendDateTimestamp) {
      const storesParam = storesArray || stores;
      estimateAudience({
        audience_filters: audienceFilters || {},
        store_ids: storesParam.map((store) => Number(store)),
        scheduled_time: sendDateTimestamp,
      });
    }
  };

  // Re-trigger estimation after remount (e.g. redirectFromPayment) when
  // the Zustand store still has a persisted audience but mutation state is lost.
  useEffect(() => {
    if (audience && !estimationData) {
      handleEstimateAudience(audience.audience_filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChooseAudience = () => {
    if (chosenAudience) {
      handleEstimateAudience(chosenAudience?.audience_filters);
    }
  };

  const handleSelectAudienceFromDropdown = (item: AudienceType) => {
    setChosenAudience(item);
    setHasCredits(true);
  };

  const parsedAudiencesData = audienceListData
    ? parseDataAudience(audienceListData, (item) => {
        handleSelectAudienceFromDropdown(item);
        setUseExistingCampaign(CREATE_CAMPAIGN_SCREEN.USE_EXISTING_VALUE);
        handleEstimateAudience(item.audience_filters);
      })
    : [];

  const creditsQuery = useGetCredits();

  const handleOpenPaymentModal = () => {
    setPaymentModalOpen(true);
    // Skip the event loop cycle and move to the next tick
    // This prevent weird error related to frames library
    // which is related to race condition with the DOM and react
    setTimeout(() => {
      setRenderPaymentUI(true);
    }, 0);
  };

  if (audienceListError || attributesError) {
    return <ResultPage />;
  }

  return (
    <TabContentWrapper tabTitle={CREATE_CAMPAIGN_SCREEN.AUDIENCE_SELECTION}>
      {contextHolder}
      <MultiSelectDropdownComponent
        options={multiSelectDataStore}
        onChange={handleStoreSelect}
        placeholder={CREATE_CAMPAIGN_SCREEN.SELECT_STORES}
        value={stores}
      />
      {stores.length > 0 && (
        <>
          <div className='choose-audience-selection-controls'>
            <ButtonComponent
              label={CREATE_CAMPAIGN_SCREEN.CREATE_NEW}
              uppercase
              onClick={handleCreateNewCampaign}
              appereance='primary'
              size='large'
              fullWidth
            />
            <DropdownSelectComponent
              items={parsedAudiencesData}
              selectLabelValue={
                chosenAudience?.name ||
                audience?.name ||
                CREATE_CAMPAIGN_SCREEN.USE_EXISTING
              }
            />
          </div>
          {useExistingCampaign === CREATE_CAMPAIGN_SCREEN.CREATE_NEW_VALUE && (
            <>
              <CreateAudienceForm
                calculateAudience={handleEstimateAudience}
                attributesList={attributesList || []}
                smsEstimation={estimationData?.sms?.estimated_reachable_users}
                pushEstimation={estimationData?.push?.estimated_reachable_users}
              />
              <ButtonComponent
                label={CREATE_CAMPAIGN_SCREEN.SAVE_AUDIENCE_TEMPLATE}
                onClick={handleSaveAudienceTemplate}
                size='large'
                uppercase
                appereance='primary'
                fullWidth
              />
              <div className='estimation-channel-cards-wrapper-container'>
                <TextComponent
                  text='Select a channel'
                  size='large'
                  weight='extra-bold'
                  center
                />
                {!chosenAudience && estimationData && (
                  <TextComponent
                    text='Please save an audience template to continue'
                    size='small'
                    center
                  />
                )}
                <div className='estimation-channel-cards-wrapper'>
                  <EstimationChannelCard
                    title='SMS'
                    disabled={
                      (!chosenAudience && !isEstimatingAudienceLoading) ||
                      estimationData?.sms?.credit_cost === 0
                    }
                    loading={isEstimatingAudienceLoading}
                    hasCredits={estimationData?.sms?.has_credits ?? false}
                    estimatedAudience={
                      estimationData?.sms?.estimated_reachable_users
                    }
                    onClick={() => {
                      if (estimationData?.sms?.has_credits) {
                        onChannelSelect('sms');
                        clearComposeFields();
                      } else {
                        handleOpenPaymentModal();
                      }
                    }}
                  />
                  <EstimationChannelCard
                    title='Push Notification'
                    disabled={
                      (!chosenAudience && !isEstimatingAudienceLoading) ||
                      estimationData?.push?.credit_cost === 0
                    }
                    loading={isEstimatingAudienceLoading}
                    hasCredits={estimationData?.push?.has_credits ?? false}
                    estimatedAudience={
                      estimationData?.push?.estimated_reachable_users
                    }
                    onClick={() => {
                      if (estimationData?.push?.has_credits) {
                        onChannelSelect('push');
                        clearComposeFields();
                      } else {
                        handleOpenPaymentModal();
                      }
                    }}
                  />
                </div>
              </div>
            </>
          )}
          {useExistingCampaign === CREATE_CAMPAIGN_SCREEN.USE_EXISTING_VALUE &&
            attributesList && (
              <UseExistingAudienceTable
                chosenAudience={chosenAudience}
                attributesList={attributesList || []}
                setChosenAudience={setChosenAudience}
                estimation={{
                  sms: estimationData?.sms ?? null,
                  push: estimationData?.push ?? null,
                }}
                onChannelSelect={(channel) => {
                  onChannelSelect(channel);
                  clearComposeFields();
                }}
                onPaymentModalOpen={handleOpenPaymentModal}
                loading={isEstimatingAudienceLoading}
              />
            )}
        </>
      )}
      <ModalComponent
        open={paymentModalOpen}
        close={() => setPaymentModalOpen(false)}
        title='Purchase Credits'
      >
        {renderPaymentUI && (
          <PurchaseCreditsTab
            creditsQuery={creditsQuery}
            onLoadingChange={setPaymentLoading}
            onClose={() => setPaymentModalOpen(false)}
            paymentFromCreateCampaign={true}
          />
        )}
      </ModalComponent>
      <FullScreenLoaderComponent
        spinning={audienceListLoading || attributesLoading || paymentLoading}
      />
    </TabContentWrapper>
  );
};
